import { Component, inject, Injectable, signal, OnDestroy } from '@angular/core';
import { CardComponent } from '../../../shared/components/card/card.component';
import { Select } from '../../../shared/components/select/select.component';
import { Button } from '../../../shared/components/button/button.component';
import { CommonModule } from '@angular/common';
import { TituloInterface } from '../../../models/titulo.model';
import { TituloService } from '../../../core/titulo.service';
import { COMPETENCIAS_ACTIONS, COMPETENCIAS_COLUMN_NAMES, COMPETENCIAS_FIELDS_CONFIG } from '../../../constants/competencias.constants';
import { defaultPaginateSelects, PaginateData, PaginateOptions } from '../../../utils/paginate.utils';
import { CompetenciaInterface } from '../../../models/competencia.model';
import { CompetenciasService } from '../../../core/competencia.service';
import { BaseSelectService } from '../../../core/BaseSelect.service';
import { Table } from '../../../shared/components/table/table.component';
import { Titledescription } from '../../../shared/components/titledescription/titledescription.component';
import { NivelService } from '../../../core/nivel.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PaginationService } from '../../../shared/services/pagination.service';
import { AutocompleteFieldComponent } from '../../../shared/components/autocomplete-field/autocomplete-field.component';

interface SelectOption {
  value: number;
  label: string;
  codigo: number | null | undefined;
}
@Injectable({
  providedIn: 'root'
})
@Component({
  selector: 'app-por-titulo',
    imports: [
      CommonModule,
      CardComponent,
      Table,
      Titledescription,
      Button,
      MatProgressSpinnerModule,
      MatFormFieldModule,
      MatInputModule,
      MatAutocompleteModule,
      FormsModule,
      ReactiveFormsModule,
      AutocompleteFieldComponent
    ],
  templateUrl: './por-titulo.component.html',
  styleUrl: './por-titulo.component.css'
})
export class PorTituloComponent implements OnDestroy {
  ngOnDestroy(): void {
    // Limpiar el cache de competencias al salir de la vista
    this.apiService._getContent.clear();
  }
  dataFetch : PaginateData<CompetenciaInterface> = {data:[],total_items:0}
  apiService = inject(CompetenciasService);
  tituloService = inject(TituloService);
  nivelService = inject(NivelService);
  dataTitulo !: TituloInterface;
  columnNamesCompetencias = COMPETENCIAS_COLUMN_NAMES;
  fieldsAddCompetencias = COMPETENCIAS_FIELDS_CONFIG;
  take: number = 100;
  skip: number = 0;
  totalItems!: number;
  titulo_id !: number
  tituloSelected!: number
  dataSelectTitulos: any[] = [];
  dataSelectNiveles: any[] = [];
  nivelSelected!: number
  actions = COMPETENCIAS_ACTIONS
  private selectService = inject(BaseSelectService<number>);
  pageIndex: number = 0;
  entityKey = 'competenciasPorTitulo';
  private paginationService = inject(PaginationService);

  isLoadingPDF = signal(false);

  async downloadCompetenciasNivelPDF() {
    this.isLoadingPDF.set(true);
    try {
      const jsPDF = await import('jspdf');
      // @ts-ignore
      const autoTable = (await import('jspdf-autotable')).default || (await import('jspdf-autotable'));
      const doc = new jsPDF.jsPDF();
      const nivel = this.dataSelectNiveles.find(n => n.value === this.nivelSelected);
      const titulo = this.dataSelectTitulos.find(t => t.value === this.tituloSelected);
      const competencias = this.dataFetch?.data?.filter((c: any) => c.nivel_id === this.nivelSelected) || [];

      // Definir columnas requeridas
      const columns = [
        { key: 'materia', label: 'Materia' },
        { key: 'modalidad', label: 'Modalidad' },
        { key: 'caracter', label: 'Carácter' }
      ];
      // Agregar columna Título Concurrente si existe al menos una competencia con ese dato
      const hasTituloConcurrente = competencias.some((c: any) => c.tituloConcurrente && c.tituloConcurrente.descripcion);
      if (hasTituloConcurrente) {
        columns.push({ key: 'tituloConcurrente', label: 'Título Concurrente' });
      }

      // Encabezados de tabla
      const head = [columns.map(col => col.label)];
      // Filas de tabla
      const body = competencias.map((comp: any) => {
        const row = [
          comp.materia?.descripcion || '',
          comp.modalidad?.descripcion || '',
          comp.caracter?.descripcion || ''
        ];
        if (hasTituloConcurrente) {
          row.push(comp.tituloConcurrente?.descripcion || '');
        }
        return row;
      });

      // Título centrado y negrita
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.text(titulo?.label || 'Título', doc.internal.pageSize.getWidth() / 2, 20, { align: 'center' });

      // Subtítulo nivel centrado y negrita
      doc.setFontSize(12);
      doc.text(`Nivel: ${nivel?.label || ''}`, doc.internal.pageSize.getWidth() / 2, 30, { align: 'center' });

      // Tabla
      autoTable(doc, {
        head: head,
        body: body,
        startY: 40,
        styles: { fontSize: 10, cellPadding: 2, overflow: 'linebreak' },
        headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
        margin: { left: 10, right: 10 },
        theme: 'grid',
        rowPageBreak: 'avoid'
      });

      doc.save(`competencias_nivel_${nivel?.label || this.nivelSelected}.pdf`);
    } finally {
      this.isLoadingPDF.set(false);
    }
  }
  isLoadingPDFFn() {
    return this.isLoadingPDF();
  }

  ngOnInit(){
    this.getTitulos();
    this.getNiveles();
    // Recupera el pageIndex desde el servicio
    this.pageIndex = this.paginationService.getPageIndex(this.entityKey);
    this.skip = this.pageIndex * this.take;
  }
  getTitulos() {
    const paginateOptions = { ...defaultPaginateSelects, take:100000};
    this.tituloService.getAllForSelect(paginateOptions, false, 'codigo').subscribe((data) => {
      this.dataSelectTitulos = data as SelectOption[];
      // Hago esto ya que tengo labels iguales y necesito diferenciarlos, el código es único y me sirve para eso
      this.dataSelectTitulos = this.dataSelectTitulos.map(t => ({
        ...t,
        label: `(${t.codigo}) ${t.label}`
      }));
      this.titulosFiltrados = this.dataSelectTitulos;
    });
  }
  getNiveles() {
    this.nivelService.getAllForSelect().subscribe((data) => {
      this.dataSelectNiveles = data;
    });
  }
  fetchData = (data: PaginateOptions , ignoreCache?:boolean) => {
    data.mainFilterField = "nivel_id";
    data.mainValueToFilter = this.nivelSelected;
    if(this.nivelSelected === 0 || this.nivelSelected === undefined) {
      data.mainFilterField = '';
      data.mainValueToFilter = '';
    }
    this.take = data.take;
    this.skip = data.skip;
    // Guarda el pageIndex en el servicio y actualiza la propiedad
    if (data.skip !== undefined && data.take !== undefined) {
      const page = Math.floor(data.skip / data.take);
      this.paginationService.setPageIndex(this.entityKey, page);
      this.pageIndex = page;
    }
    return this.apiService.getByTitulo(data,this.tituloSelected,ignoreCache).subscribe(data => {
      this.dataFetch = { ...data };
      this.totalItems = this.dataFetch.total_items;
    });
  }
  selectValueTitulo(value: number) {
    this.tituloSelected = value;
    this.selectService.setValue(value);
    this.nivelSelected = undefined as any;
    this.dataFetch = {data:[],total_items:0};
    // Si ya hay nivel seleccionado, buscar datos automáticamente
    if (this.nivelSelected) {
      this.fetchData({ skip: this.skip, take: 50 });
    }
  }
  selectValueNivel(value: number) {
    this.nivelSelected = value;
    if (this.tituloSelected != null && this.nivelSelected != null) {
      this.fetchData({ skip: this.skip, take: 50 });
    }
  }

  // Para mat-autocomplete de títulos
  tituloInputText: string = '';
  tituloInput!: SelectOption;
  titulosFiltrados: any[] = [];

  filtrarTitulos(input: string) {
    if (typeof input !== 'string') {
      return;
    }
    const filtro = input?.toLowerCase() || '';
    this.titulosFiltrados = this.dataSelectTitulos.filter((t: any) =>
      t.label.toLowerCase().includes(filtro) ||
      (t.codigo !== null && t.codigo !== undefined && t.codigo.toString().includes(filtro))
    );
  }

  onTituloSelected(event: any) {
    const label = event.option.value;
    const seleccionado = this.dataSelectTitulos.find((t: any) => t.label === label);
    if (seleccionado) {
      this.tituloSelected = seleccionado.value;
      this.selectService.setValue(seleccionado.value);
      this.nivelSelected = undefined as any;
      this.dataFetch = {data:[],total_items:0};
      // Limpiar input tras seleccionar
      this.tituloInput = label;
      // Si ya hay nivel seleccionado, buscar datos automáticamente
      // if (this.nivelSelected) {
        this.fetchData({ skip: this.skip, take: 50 });
      // }
    }
  }
}
